import React, { useEffect } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

const sections = [
  {
    title: '1. About this Privacy Policy',
    paragraphs: [
      'This Privacy Policy explains how JourneyBot collects, uses, stores, and protects information when you use JourneyBot.',
      'JourneyBot is a private planning workspace that helps users create, organise, save, and review personal journey data. This may include goals, notes, decisions, blockers, action items, summaries, and AI-generated content that users choose to import.',
      'JourneyBot is currently an MVP. Features may change as the product develops.',
    ],
  },
  {
    title: '2. Information we collect',
    paragraphs: ['JourneyBot may collect the following types of information:'],
    groups: [
      {
        label: 'Account information:',
        items: [
          'email address;',
          'login provider information, such as Google sign-in details;',
          'authentication identifiers needed to keep your account secure.',
        ],
      },
      {
        label: 'Journey data you create:',
        items: [
          'goals;',
          'context;',
          'assumptions;',
          'constraints;',
          'decisions;',
          'action items;',
          'blockers;',
          'open questions;',
          'notes;',
          'summaries;',
          'imported AI-generated content;',
          'other information you choose to enter into JourneyBot.',
        ],
      },
      {
        label: 'Technical information:',
        items: [
          'device, browser, and session information;',
          'error logs;',
          'security logs;',
          'usage events needed to operate, debug, secure, and improve the service.',
        ],
      },
      {
        label: 'Support information:',
        items: [
          'messages you send to us;',
          'feedback, bug reports, or support requests.',
        ],
      },
    ],
  },
  {
    title: '3. Information you choose to enter',
    paragraphs: [
      'You control what journey data you enter into JourneyBot.',
      'Your journey data may contain personal, financial, family, career, property, or other sensitive information depending on what you choose to write.',
      'Do not enter information into JourneyBot unless you are comfortable storing it under this Privacy Policy.',
      'JourneyBot is a planning workspace, not a professional advice service.',
    ],
  },
  {
    title: '4. Local storage and cloud storage',
    paragraphs: ['JourneyBot may store data locally on your device, in cloud storage, or both.'],
    groups: [
      {
        label: 'Local storage:',
        paragraphs: [
          'Journey data may be saved in your browser or device storage. If you clear browser data, lose your device, change browsers, or uninstall the app, local data may be lost.',
        ],
      },
      {
        label: 'Cloud storage:',
        paragraphs: [
          'If you sign in and choose to save or sync your journey to cloud storage, your journey data may be stored remotely so you can reload it after login or access it across devices.',
          'Cloud storage may include user-created journey data and imported AI-generated content.',
        ],
      },
    ],
  },
  {
    title: '5. How we use information',
    paragraphs: ['JourneyBot may use information to:'],
    items: [
      'create and manage your account;',
      'authenticate users;',
      'save, load, edit, and display journey data;',
      'provide local and cloud storage features;',
      'help you export or delete data where supported;',
      'debug errors and improve reliability;',
      'protect the service from misuse, fraud, abuse, and security threats;',
      'respond to support requests;',
      'improve the product.',
    ],
  },
  {
    title: '6. AI-generated and imported content',
    paragraphs: [
      'JourneyBot may allow you to paste or import AI-generated content from third-party AI tools.',
      'If you import AI-generated content, JourneyBot treats that content as user-created journey data.',
      'You are responsible for reviewing AI-generated content before saving, using, or relying on it.',
      'JourneyBot does not control third-party AI tools and is not responsible for their privacy, security, accuracy, or data handling practices.',
    ],
  },
  {
    title: '7. Third-party service providers',
    paragraphs: ['JourneyBot may use third-party providers to operate the service. These may include:'],
    items: [
      'hosting providers;',
      'database providers;',
      'authentication providers;',
      'cloud infrastructure providers;',
      'analytics or monitoring providers;',
      'email or support providers;',
      'AI providers, if AI features are enabled.',
    ],
    after: [
      'These providers may process information only as needed to provide their services to JourneyBot.',
      'For the MVP, JourneyBot may use Firebase, Firebase Authentication, Firestore, Google sign-in, and related Google Cloud services.',
    ],
  },
  {
    title: '8. Early Access development and testing',
    paragraphs: [
      'During Early Access, JourneyBot collects account information and journey information to create accounts, authenticate users, save and load journey data, provide import and export features, debug errors, improve reliability, protect the service, and respond to support requests.',
      'The codebase currently uses Firebase Authentication for account login and Firestore for cloud storage of account-level profile metadata and user journey data. Google sign-in is available as an authentication provider.',
      '[Confirm whether journey content is sent to third-party AI providers.]',
      'Diagnostic logs and error-reporting data may include device, browser, session, security, usage, and error details needed to operate, debug, secure, and improve JourneyBot.',
      'Support, debugging, and security access may require authorised maintainers or service providers to access account, diagnostic, or journey information where necessary to investigate a request, fix a defect, protect the service, or comply with law.',
      'JourneyBot retains information for as long as needed to provide the service, meet legal obligations, resolve disputes, maintain security, and improve reliability. Users may request access, correction, export, or deletion using the contact details below.',
      '[Confirm whether production user content is used in development or test environments.]',
    ],
  },
  {
    title: '9. When we share information',
    paragraphs: [
      'JourneyBot does not sell your journey data.',
      'JourneyBot may share information only where needed to:',
    ],
    items: [
      'operate the service through trusted service providers;',
      'comply with law, regulation, legal process, or government request;',
      'investigate security, fraud, abuse, or technical issues;',
      'protect the rights, safety, or property of JourneyBot, users, or others;',
      'complete a business transfer, such as a merger, acquisition, restructure, or sale of assets.',
    ],
  },
  {
    title: '10. Data security',
    paragraphs: [
      'JourneyBot uses reasonable technical and organisational measures designed to protect user information.',
      'However, no method of storage, cloud service, browser storage, authentication, or internet transmission is completely secure.',
      'You are responsible for protecting your account login details and controlling access to your devices.',
    ],
  },
  {
    title: '11. Data retention',
    paragraphs: [
      'JourneyBot keeps information for as long as needed to provide the service, comply with legal obligations, resolve disputes, maintain security, and improve reliability.',
      'If you delete journey data or your account, some information may remain for a limited period in backups, logs, cached systems, or third-party infrastructure where technically or legally required.',
      'After deletion, your data may not be recoverable.',
    ],
  },
  {
    title: '12. Access, export, correction, and deletion',
    paragraphs: ['Where supported, JourneyBot may allow you to:'],
    items: [
      'access your journey data;',
      'correct or edit your journey data;',
      'export your journey data;',
      'delete local journey data;',
      'delete cloud journey data;',
      'close your account.',
    ],
    after: ['If these tools are not yet available in the MVP, you may contact us using the details below.'],
  },
  {
    title: '13. Children',
    paragraphs: [
      'JourneyBot is not intended for children under 13.',
      'If we become aware that we have collected personal information from a child under 13 without appropriate consent, we will take reasonable steps to delete it.',
    ],
  },
  {
    title: '14. Overseas storage and processing',
    paragraphs: [
      'JourneyBot may use cloud providers that store or process information in Australia or overseas.',
      'By using JourneyBot, you acknowledge that your information may be processed or stored outside your country of residence where our service providers operate.',
    ],
  },
  {
    title: '15. Data breaches',
    paragraphs: [
      'If JourneyBot becomes aware of a data breach, we will assess the issue and take reasonable steps to contain, investigate, and respond to it.',
      'Where required by law, we will notify affected users and relevant regulators.',
    ],
  },
  {
    title: '16. Changes to this Privacy Policy',
    paragraphs: [
      'JourneyBot may update this Privacy Policy from time to time.',
      'The updated version will be posted in JourneyBot with a new "Last updated" date.',
      'If a change materially affects how your information is handled, we will try to provide reasonable notice.',
    ],
  },
];

function PolicyList({ items }) {
  if (!items?.length) return null;

  return (
    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
      {items.map((item) => (
        <Typography component="li" variant="body1" key={item} sx={{ mb: 0.5 }}>
          {item}
        </Typography>
      ))}
    </Box>
  );
}

function Paragraphs({ paragraphs }) {
  if (!paragraphs?.length) return null;

  return paragraphs.map((paragraph) => (
    <Typography variant="body1" paragraph key={paragraph}>
      {paragraph}
    </Typography>
  ));
}

function PrivacyPolicy() {
  useEffect(() => {
    document.title = 'JourneyBot Privacy Policy';
  }, []);

  return (
    <Box sx={{ bgcolor: 'background.default', color: 'text.primary', textAlign: 'left', py: { xs: 4, md: 7 } }}>
      <Container maxWidth="md">
        <Stack spacing={4}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 900, mb: 1 }}>
              JourneyBot Privacy Policy
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Last updated: July 11, 2026
            </Typography>
          </Box>

          {sections.map((section) => (
            <Box component="section" key={section.title}>
              <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mb: 1.5 }}>
                {section.title}
              </Typography>
              <Paragraphs paragraphs={section.paragraphs} />
              <PolicyList items={section.items} />
              {section.groups?.map((group) => (
                <Box key={group.label} sx={{ mt: 2 }}>
                  <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 800 }}>
                    {group.label}
                  </Typography>
                  <Paragraphs paragraphs={group.paragraphs} />
                  <PolicyList items={group.items} />
                </Box>
              ))}
              <Paragraphs paragraphs={section.after} />
            </Box>
          ))}

          <Box component="section">
            <Typography variant="h5" component="h2" sx={{ fontWeight: 800, mb: 1.5 }}>
              17. Contact
            </Typography>
            <Typography variant="body1" paragraph>
              For privacy questions, data access, correction, deletion, or support requests, contact:
            </Typography>
            <Typography variant="body1">
              <Link href="mailto:info@thedrunkbanana.com">info@thedrunkbanana.com</Link>
              <br />
              Oceanic Creative
              <br />
              NSW, Australia
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
}

export default PrivacyPolicy;
